import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account } from '../../models/account';
import { AccountFilter } from '../../models/account.filter';
import { BankAccount } from '../../models/bank-account';
import { Currency } from '../../models/currency';
import { KycDocument } from '../../models/document';
import { Wallet } from '../../models/wallet';
import { BaseApiService } from '@core/http/base.api.service';
import { ConfigService } from '@core/services/config.service';
import { PageList } from '@core/models/page-list';

@Injectable()
export class AccountApiService extends BaseApiService {
  constructor(public http: HttpClient, private configService: ConfigService) {
    super(http);
  }

  get baseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.payment}/api/account`;
  }

  public getStripeClientSecret(id: string): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/${id}/clientsecret`);
  }

  public getAllAccount(
    search: string,
    filter: AccountFilter = new AccountFilter(),
    pageOffset: number = 0,
    pageSize: number = 10
  ): Observable<PageList<Account>> {
    const params = new HttpParams()
      .append('name', search)
      .append('userId', filter.userId ? filter.userId : '')
      .append('organizationId', filter.organizationId ? filter.organizationId : '')
      .append('pageOffset', `${pageOffset}`)
      .append('pageSize', `${pageSize}`)
      .append('onlyActive', filter.onlyActive ? 'true' : 'false');

    return super.getAllWithPagination<Account>(this.baseUrl, params);
  }

  public getAccountById(id: string): Observable<Account> {
    return super.get(this.baseUrl, id);
  }

  public fetchCurrentAccounts(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.baseUrl}/me`).pipe(
      map((accounts) => {
        return accounts;
      })
    );
  }

  public getWalletByProvider(provider: string, providerId: string): Observable<Wallet> {
    return this.http.get<Wallet>(`${this.baseUrl}/wallet/${provider}/${providerId}`);
  }

  public registerAccount(account: Account): Observable<Account> {
    return this.http.post<Account>(this.baseUrl, account);
  }

  public updateAccount(id: string, account: Partial<Account>): Observable<Account> {
    return this.http.put<Account>(`${this.baseUrl}/${id}`, account);
  }

  public registerAccountWallet(accountId: string, wallet: Wallet): Observable<Wallet> {
    return this.http.post<Wallet>(`${this.baseUrl}/${accountId}/wallet`, wallet);
  }

  public getCurrency(search: string = '', pageOffset: number = 0, pageSize: number = 100): Observable<Currency[]> {
    const params = new HttpParams()
      .append('search', search)
      .append('pageOffset', `${pageOffset}`)
      .append('pageSize', `${pageSize}`);

    return this.http.get<Currency[]>(`${this.configService.getCurrent().services.payment}/api/currency`, {
      params: params,
    });
  }

  public fetchKycDocuments(accountId: string): Observable<KycDocument[]> {
    return this.http.get<KycDocument[]>(`${this.baseUrl}/${accountId}/document`);
  }

  public uploadKYC(accountId: string, documentId: string, files: File[], kycType: string): Observable<void> {
    const param = new HttpParams().append('documentType', kycType);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return this.http.post<void>(`${this.baseUrl}/${accountId}/document/${documentId}`, formData, { params: param });
  }

  public submitKycDocument(accountId: string, documentId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${accountId}/document/${documentId}/submit`, null);
  }

  public addIBANToStripe(accountId: string, token: string): Observable<BankAccount> {
    return this.http.post<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount`, { bankAccountToken: token });
  }

  public registerBankAccount(accountId: string, bankAccount: BankAccount): Observable<BankAccount> {
    return this.http.post<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount`, bankAccount);
  }

  // tslint:disable-next-line: max-line-length
  public updateBankAccount(
    accountId: string,
    bankAccountId: string,
    bankAccount: Partial<BankAccount>
  ): Observable<BankAccount> {
    return this.http.put<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount/${bankAccountId}`, bankAccount);
  }

  public getBankAccount(accountId?: string): Observable<BankAccount[]> {
    return this.http.get<BankAccount[]>(`${this.baseUrl}/${accountId}/bankaccount`);
  }

  public getBankAccountById(accountId: string, bankAccountId: string): Observable<BankAccount> {
    return this.http.get<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount/${bankAccountId}`);
  }

  public uploadDocument(
    accountId: string,
    documentId: string,
    docRef: string,
    files: File[],
    documentType: string,
    documentSide: string
  ): Observable<void> {
    const param = new HttpParams()
      .append('documentType', documentType)
      .append('documentRef', docRef)
      .append('documentSide', documentSide);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return this.http.post<void>(`${this.baseUrl}/${accountId}/document/${documentId}`, formData, { params: param });
  }

  public uploadKycDocument(
    accountId: string,
    documentId: string,
    docRef: string,
    file: File,
    documentType: string,
    identityType: string,
    documentSide: string
  ): Observable<KycDocument> {
    const param = new HttpParams()
      .append('documentId', documentId)
      .append('documentType', documentType)
      .append('identityType', identityType)
      .append('documentRef', docRef)
      .append('documentSide', documentSide);

    const formData = new FormData();
    /* files.forEach(file => {
            formData.append('files', file);
        }); */
    formData.append('files', file);
    return this.http.post<KycDocument>(`${this.baseUrl}/${accountId}/document/${documentId}`, formData, {
      params: param,
    });
  }
}
